import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { COOKIE_CONSENT_KEY } from '@/utils/cookieConsent';
import { clearBehaviorTrackingData } from '@/utils/behaviorTracking';
import { loadMetaPixelWhenConsented } from '@/utils/metaPixel';

export const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Alltid aktiv
    statistics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Visa banner efter kort fördröjning för bättre UX
      setTimeout(() => setIsVisible(true), 1000);
    } else {
      const prefs = JSON.parse(consent);
      setPreferences(prefs);
      loadMetaPixelWhenConsented();
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setIsVisible(false);
    setShowSettings(false);

    if (!prefs.statistics) {
      clearBehaviorTrackingData();
    }
    loadMetaPixelWhenConsented();
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      statistics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    });
  };

  const acceptNecessaryOnly = () => {
    saveConsent({
      necessary: true,
      statistics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    });
  };

  const savePreferences = () => {
    saveConsent({
      ...preferences,
      timestamp: new Date().toISOString()
    });
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      data-testid="cookie-banner"
    >
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        {!showSettings ? (
          // Huvudbanner
          <div className="p-4 md:p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-[#FF8A00]/10 rounded-full flex items-center justify-center">
                <Cookie className="h-5 w-5 text-[#FF8A00]" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-[#0F172A] text-lg mb-2">
                  Vi använder cookies
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Vi använder cookies för att förbättra din upplevelse på ByggFynd. 
                  Nödvändiga cookies är alltid aktiva för att webbplatsen ska fungera. 
                  Du kan välja att acceptera eller neka statistik- och marknadsföringscookies.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button 
                    onClick={acceptAll}
                    className="bg-[#003B73] hover:bg-[#002a52] text-white"
                    data-testid="accept-all-cookies"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Acceptera alla
                  </Button>
                  <Button 
                    onClick={acceptNecessaryOnly}
                    variant="outline"
                    className="border-gray-300"
                    data-testid="accept-necessary-cookies"
                  >
                    Endast nödvändiga
                  </Button>
                  <Button 
                    onClick={() => setShowSettings(true)}
                    variant="ghost"
                    className="text-[#003B73]"
                    data-testid="cookie-settings-btn"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Inställningar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Inställningar
          <div className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#0F172A] text-lg">Cookie-inställningar</h3>
              <button 
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              {/* Nödvändiga cookies */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold text-[#0F172A]">Nödvändiga cookies</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Dessa cookies är nödvändiga för att webbplatsen ska fungera och kan inte stängas av. 
                    De inkluderar inloggning, varukorg och grundläggande funktioner.
                  </p>
                </div>
                <div className="ml-4">
                  <div className="w-12 h-6 bg-[#003B73] rounded-full flex items-center justify-end px-1">
                    <div className="w-4 h-4 bg-white rounded-full" />
                  </div>
                  <span className="text-xs text-gray-500 mt-1 block text-center">Alltid på</span>
                </div>
              </div>

              {/* Statistik cookies */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold text-[#0F172A]">Statistik & analys</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Dessa cookies hjälper oss att förstå hur besökare använder webbplatsen 
                    genom att samla in anonym information.
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => setPreferences({ ...preferences, statistics: !preferences.statistics })}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.statistics ? 'bg-[#003B73] justify-end' : 'bg-gray-300 justify-start'
                    }`}
                    data-testid="toggle-statistics"
                  >
                    <div className="w-4 h-4 bg-white rounded-full" />
                  </button>
                </div>
              </div>

              {/* Marknadsföring cookies */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold text-[#0F172A]">Marknadsföring</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Dessa cookies används för att visa relevanta annonser och mäta 
                    annonsernas effektivitet.
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.marketing ? 'bg-[#003B73] justify-end' : 'bg-gray-300 justify-start'
                    }`}
                    data-testid="toggle-marketing"
                  >
                    <div className="w-4 h-4 bg-white rounded-full" />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                onClick={savePreferences}
                className="bg-[#003B73] hover:bg-[#002a52] text-white"
                data-testid="save-cookie-preferences"
              >
                Spara inställningar
              </Button>
              <Button 
                onClick={acceptAll}
                variant="outline"
              >
                Acceptera alla
              </Button>
            </div>

            <p className="text-xs text-gray-500 mt-4">
              Läs mer i vår <a href="/integritetspolicy" className="text-[#003B73] hover:underline">integritetspolicy</a>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Hook för att läsa cookie-samtycke
export const useCookieConsent = () => {
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      setConsent(JSON.parse(stored));
    }
  }, []);

  const updateConsent = () => {
    // Öppna cookie-inställningar
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    window.location.reload();
  };

  return { consent, updateConsent };
};

export default CookieBanner;
